import React from "react";
import { Box, Typography } from "@mui/material";
import SelectableChipList from "../SelectableChipList";
import type { MedicationSuggestionGroup } from "../../../utils/AIPrescription/prescriptionUtils";

interface Suggestion {
  text?: string;
  suggestion?: string;
  value?: string;
  [key: string]: any;
}

interface SuggestionsProps {
  suggestions: (string | Suggestion)[];
  suggestionGroups?: MedicationSuggestionGroup[];
  selectedPicksByMedication?: Record<string, string>;
  primaryColor: string;
  onSuggestionSelect: (suggestionText: string, medicationName?: string) => void;
  label?: string;
  /** True when a pharmacy picker from the same turn is also awaiting a pick. */
  pendingOtherPicker?: boolean;
  /** True on a past (non-latest) turn: render read-only, chosen chip still highlighted. */
  disabled?: boolean;
}

const Suggestions: React.FC<SuggestionsProps> = ({
  suggestions,
  suggestionGroups,
  selectedPicksByMedication = {},
  primaryColor,
  onSuggestionSelect,
  label = "Suggestions:",
  pendingOtherPicker = false,
  disabled = false,
}) => {
  const groups =
    suggestionGroups && suggestionGroups.length > 0
      ? suggestionGroups
      : null;

  if (groups) {
    const multiMed = groups.length > 1;
    const helperText = pendingOtherPicker
      ? "Pick one strength below, and a pharmacy below."
      : multiMed
      ? "Pick one strength under each medication below (selected chips are highlighted)."
      : "Choose one strength below.";
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 2, mb: 2 }}>
        <Typography variant="body2" sx={{ color: "#495057", lineHeight: 1.5 }}>
          {helperText}
        </Typography>
        {groups.map((group) => (
          <SelectableChipList
            key={group.medication_name}
            items={group.options}
            primaryColor={primaryColor}
            onItemSelect={(text) => onSuggestionSelect(text, group.medication_name)}
            label={`${group.medication_name}:`}
            selectedItems={
              selectedPicksByMedication[group.medication_name]
                ? [selectedPicksByMedication[group.medication_name]]
                : []
            }
            disabled={disabled}
          />
        ))}
      </Box>
    );
  }

  return (
    <SelectableChipList
      items={suggestions}
      primaryColor={primaryColor}
      onItemSelect={(text) => onSuggestionSelect(text)}
      label={label}
      disabled={disabled}
    />
  );
};

export default Suggestions;
